import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertTriangle, MailX, RefreshCw, Trash2, Users, Mail,
  ShieldAlert, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { emailSuppressionAPI } from '../../services/api';

const REASON_LABELS = {
  hard_bounce: { label: 'Hard Bounce', color: 'bg-red-100 text-red-700 border-red-200' },
  complaint: { label: 'Spam Complaint', color: 'bg-orange-100 text-orange-700 border-orange-200' },
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmailSuppressionPage() {
  const [suppressed, setSuppressed] = useState([]);
  const [bouncedUsers, setBouncedUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('suppressed');
  const [reasonFilter, setReasonFilter] = useState('');
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [suppRes, statsRes, usersRes] = await Promise.all([
        emailSuppressionAPI.list(reasonFilter || undefined),
        emailSuppressionAPI.stats(),
        emailSuppressionAPI.bouncedUsers(),
      ]);
      setSuppressed(suppRes.data);
      setStats(statsRes.data);
      setBouncedUsers(usersRes.data);
    } catch (err) {
      toast.error('Failed to load suppression data');
    } finally {
      setLoading(false);
    }
  }, [reasonFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the suppression list? Future emails to this address will be attempted again.`)) return;
    setRemoving(email);
    try {
      await emailSuppressionAPI.remove(email);
      toast.success(`${email} removed from suppression list`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove address');
    } finally {
      setRemoving(null);
    }
  };

  const fmt = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  };

  const tabs = [
    { id: 'suppressed', label: 'Suppressed Addresses', count: suppressed.length },
    { id: 'users', label: 'Affected Users', count: bouncedUsers.length },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Suppression</h1>
            <p className="text-sm text-gray-500 mt-1">
              Addresses blocked due to hard bounces or spam complaints
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard icon={MailX} label="Total Suppressed" value={stats?.total} color="bg-red-50 text-red-600" />
          <StatCard icon={AlertTriangle} label="Hard Bounces" value={stats?.hard_bounce} color="bg-orange-50 text-orange-600" />
          <StatCard icon={ShieldAlert} label="Complaints" value={stats?.complaint} color="bg-yellow-50 text-yellow-600" />
          <StatCard icon={Users} label="Affected Users" value={bouncedUsers.length} color="bg-blue-50 text-blue-600" />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Suppressed addresses tab */}
        {activeTab === 'suppressed' && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Suppressed Email Addresses</CardTitle>
                  <CardDescription>
                    These addresses will not receive any email from Assura until removed.
                  </CardDescription>
                </div>
                {/* Reason filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select
                    value={reasonFilter}
                    onChange={(e) => setReasonFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded px-2 py-1 text-gray-700"
                  >
                    <option value="">All reasons</option>
                    <option value="hard_bounce">Hard Bounce</option>
                    <option value="complaint">Spam Complaint</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
              ) : suppressed.length === 0 ? (
                <div className="p-8 text-center">
                  <Mail className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No suppressed addresses{reasonFilter ? ' for this filter' : ''}.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-y border-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Email Address</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Reason</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Suppressed At</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {suppressed.map((row) => {
                        const reason = REASON_LABELS[row.reason] || { label: row.reason, color: 'bg-gray-100 text-gray-600' };
                        return (
                          <tr key={row.email} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-gray-800">{row.email}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${reason.color}`}>
                                {reason.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-500">{fmt(row.suppressed_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemove(row.email)}
                                disabled={removing === row.email}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {removing === row.email ? 'Removing…' : 'Remove'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Affected users tab */}
        {activeTab === 'users' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Users with Delivery Issues</CardTitle>
              <CardDescription>
                Platform users whose email address has bounced or filed a spam complaint.
                Removing the address from the suppression list above also clears these flags.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
              ) : bouncedUsers.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No users with delivery issues.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-y border-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">User</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Role</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Issue</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Since</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-600">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {bouncedUsers.map((user) => {
                        const isBounced = user.email_bounced;
                        const isComplained = user.email_complained;
                        const since = isBounced ? user.email_bounced_at : user.email_complained_at;
                        return (
                          <tr key={user.id || user.email} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-gray-800">{user.full_name || '—'}</p>
                              <p className="text-xs text-gray-400 font-mono">{user.email}</p>
                            </td>
                            <td className="px-4 py-3 capitalize text-gray-600">
                              {(user.role || '').replace('_', ' ')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 flex-wrap">
                                {isBounced && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200">
                                    Hard Bounce
                                  </span>
                                )}
                                {isComplained && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-orange-100 text-orange-700 border-orange-200">
                                    Complaint
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-500">{fmt(since)}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemove(user.email)}
                                disabled={removing === user.email}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {removing === user.email ? 'Removing…' : 'Remove'}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
